/**
 * Billing Success Page
 * Shown after successful Stripe checkout completion
 */
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';

/**
 * Success page displayed after successful subscription checkout.
 * Shows a success message with confetti effect and links to dashboard.
 */
export default function BillingSuccess() {
  const [searchParams] = useSearchParams();
  const [showConfetti, setShowConfetti] = useState(false);

  // Get the session ID from URL if present (for potential verification)
  const sessionId = searchParams.get('session_id');

  // Trigger confetti animation on mount
  useEffect(() => {
    setShowConfetti(true);
    // Hide confetti after animation completes
    const timer = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      {/* Confetti effect */}
      {showConfetti && <ConfettiEffect />}

      <Card className="max-w-md w-full text-center">
        <div className="py-8 px-6">
          {/* Success icon */}
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
            <CheckIcon className="h-8 w-8 text-green-600" />
          </div>

          {/* Success message */}
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Welcome to FlowIO Pro!
          </h1>
          <p className="text-gray-600 mb-8">
            Your subscription is now active. You have access to all premium features.
          </p>

          {/* Feature highlights */}
          <div className="bg-gray-50 rounded-lg p-4 mb-8">
            <p className="text-sm font-medium text-gray-700 mb-3">
              What's included:
            </p>
            <ul className="space-y-2 text-left">
              {[
                'Unlimited document uploads',
                'Advanced flow visualizations',
                'Priority customer support',
                'Export your data anytime',
              ].map((feature) => (
                <li key={feature} className="flex items-center text-sm text-gray-600">
                  <CheckIcon className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          {/* CTA buttons */}
          <div className="space-y-3">
            <Link to="/dashboard" className="block">
              <Button variant="primary" className="w-full">
                Go to Dashboard
              </Button>
            </Link>
            <Link to="/import" className="block">
              <Button variant="outline" className="w-full">
                Import Documents
              </Button>
            </Link>
          </div>

          {/* Session ID for debugging (hidden in production) */}
          {sessionId && import.meta.env.DEV && (
            <p className="mt-6 text-xs text-gray-400">
              Session: {sessionId.substring(0, 20)}...
            </p>
          )}
        </div>
      </Card>
    </div>
  );
}

// ============================================================================
// Icon Components
// ============================================================================

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4.5 12.75l6 6 9-13.5"
      />
    </svg>
  );
}

// ============================================================================
// Confetti Effect Component
// ============================================================================

function ConfettiEffect() {
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-50" aria-hidden="true">
      {/* Create multiple confetti pieces */}
      {Array.from({ length: 50 }).map((_, i) => (
        <div
          key={i}
          className="absolute animate-confetti"
          style={{
            left: `${Math.random() * 100}%`,
            top: '-10px',
            animationDelay: `${Math.random() * 0.5}s`,
            animationDuration: `${2 + Math.random() * 2}s`,
          }}
        >
          <div
            className="w-3 h-3 transform rotate-45"
            style={{
              backgroundColor: [
                '#fbbf24', // amber
                '#34d399', // green
                '#60a5fa', // blue
                '#f472b6', // pink
                '#a78bfa', // purple
                '#fb923c', // orange
              ][Math.floor(Math.random() * 6)],
            }}
          />
        </div>
      ))}

      {/* CSS animation for confetti */}
      <style>{`
        @keyframes confetti-fall {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        .animate-confetti {
          animation: confetti-fall linear forwards;
        }
      `}</style>
    </div>
  );
}
